import { Rs232Controller } from './Rs232Controller';
import { $ } from '../../../vendor/jquery';

/* global REACH */

/**
 * converts a uint8array into a string
 * @param {Uint8Array} u8a
 * @returns {string}
 * @private
 */
// eslint-disable-next-line no-unused-vars
function _uint8ToString(u8a) {
    var CHUNK_SZ = 0x8000;
    var c = [];
    for (var i = 0; i < u8a.length; i += CHUNK_SZ) {
        c.push(String.fromCharCode.apply(null, u8a.subarray(i, i + CHUNK_SZ)));
    }

    return c.join('');
}

/**
 * converts a uint8array into a hex-encoded string
 * @param {Uint8Array} u8a
 * @returns {string}
 * @private
 */
function _uint8ToHexString(u8a) {
    return Array.from(u8a, function (byte) {
        return ('0' + (byte & 0xff).toString(16)).slice(-2);
    }).join('');
}

/**
 * REACH Rs232Controller
 * @class
 * @augments Rs232Controller
 * @param {string} comPort - the name of the COM port to open
 * @param {ComSettings} comSettings - settings for the
 * com port
 */
function ReachPlayerRs232Controller(comPort, comSettings) {
    Rs232Controller.call(this, comPort, comSettings);

    this._readTimeout = this.readTimeout;
    this.comPortResourceId = REACH.ComPort.CreateComPort(this.comPort);

    //TODO: translate string enums to int values for C#
    REACH.ComPort.SetBaudRate(this.comPortResourceId, this.baudRate);
    //REACH.ComPort.SetDataBits(this.comPortResourceId, this.dataBits);
    //REACH.ComPort.SetStopBits(this.comPortResourceId, this.stopBits);
    //REACH.ComPort.SetParity(this.comPortResourceId, this.parity);
    REACH.ComPort.SetDiscardNull(this.comPortResourceId, this.discardNull);
    REACH.ComPort.SetEncoding(this.comPortResourceId, this.encoding);
    //REACH.ComPort.SetHandshake(this.comPortResourceId, this.handshake);
    REACH.ComPort.SetNewLine(this.comPortResourceId, this.newLine);
    REACH.ComPort.SetReadBufferSize(this.comPortResourceId, this.readBufferSize);
    REACH.ComPort.SetReadTimeout(this.comPortResourceId, this.readTimeout);
    REACH.ComPort.SetReadBytesThreshold(this.comPortResourceId, this.readBytesThreshold);
    REACH.ComPort.SetDtrEnable(this.comPortResourceId, this.dtrEnabled);
    REACH.ComPort.SetRtsEnable(this.comPortResourceId, this.rtsEnabled);
    REACH.ComPort.SetWriteBufferSize(this.comPortResourceId, this.writeBufferSize);
    REACH.ComPort.SetWriteTimeout(this.comPortResourceId, this.writeTimeout);

    Object.defineProperty(this, 'readTimeout', {
        get: function () {
            return this._readTimeout;
        },
        set: function (value) {
            this._readTimeout = value;
            REACH.ComPort.SetReadTimeout(this.comPortResourceId, value);
        },
    });
}

ReachPlayerRs232Controller.prototype = Object.create(Rs232Controller.prototype);
ReachPlayerRs232Controller.prototype.constructor = ReachPlayerRs232Controller;

/**
 * opens a connection to the COM port using internal settings
 */
ReachPlayerRs232Controller.prototype.open = function () {
    REACH.ComPort.Open(this.comPortResourceId);
};

/**
 * closes a connection to the COM port
 */
ReachPlayerRs232Controller.prototype.close = function () {
    REACH.ComPort.CloseComPort(this.comPortResourceId);
};

/**
 * writes an array of bytes to the COM port
 * @param {Uint8Array} data - the data to write to the port
 */
ReachPlayerRs232Controller.prototype.write = function (data) {
    //note: REACH.ComPort.Write accepts message as string, thus null bytes will
    //      break! We will use WriteHexEncodedString instead which will be safe
    //      (but slower)
    REACH.ComPort.WriteHexEncodedString(this.comPortResourceId, _uint8ToHexString(data));
};

/**
 * writes an array of bytes to the COM port with an newLine character
 * @param {Uint8Array} data - the data to write to the port
 */
ReachPlayerRs232Controller.prototype.writeLine = function (data) {
    REACH.ComPort.WriteLine(this.comPortResourceId, data);
};

/**
 * reads numBytes bytes from the COM port
 * @param {number} numBytes - the number of bytes to read from the COM port
 * @returns {Promise<int[]>}
 *      Success `Promise[int[]]`  <br />
 *      Failure `Promise`
 */
ReachPlayerRs232Controller.prototype.read = function (numBytes) {
    var jqDeferred = $.Deferred();

    REACH.ComPort.Read(
        this.comPortResourceId,
        numBytes,
        function (result) {
            jqDeferred.resolve(result);
        },
        jqDeferred.reject
    );

    return jqDeferred.promise();
};

/**
 * reads bytes from the COM port until newLine is encountered
 * @returns {Promise<int[]>}
 *      Success `Promise[int[]]`  <br />
 *      Failure `Promise`
 */
ReachPlayerRs232Controller.prototype.readLine = function (bytesThusFar, jqDeferred, iteration) {
    iteration = iteration || 0;

    if (typeof bytesThusFar === 'undefined') {
        bytesThusFar = [];
    }

    if (!jqDeferred) {
        jqDeferred = $.Deferred();
    }

    if (++iteration > 1000) {
        jqDeferred.reject('too many iterations');

        return jqDeferred.promise();
    }

    try {
        REACH.ComPort.Read(
            this.comPortResourceId,
            1,
            function (bytes) {
                if (bytes.length > 0) {
                    var byte = bytes[0];

                    if (byte === this.newLine.charCodeAt(0)) {
                        jqDeferred.resolve(bytesThusFar);
                    } else {
                        bytesThusFar.push(bytes[0]);

                        this.readLine(bytesThusFar, jqDeferred, iteration);
                    }
                }
            }.bind(this),
            function () {
                jqDeferred.reject();
            }.bind(this)
        );
    } catch (e) {
        jqDeferred.reject();
    }

    return jqDeferred.promise();
};

export { ReachPlayerRs232Controller as ReachRs232Controller };
